import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { FeatureCollection } from 'geojson';
import Map, { SLFMapHandle } from "../../components/models/spotted-lanternfly-vineyard-risk/map";
import Header from "../../components/models/spotted-lanternfly-vineyard-risk/Header";
import SubHeader from "../../components/models/spotted-lanternfly-vineyard-risk/SubHeader";
import StationsListing from "../../components/stationsListingV3";
import { useTracking } from "../../utils/hooks/useTracking";
import useStations from "../../utils/hooks/useStations";
// @ts-ignore
import Metadata from "./spotted-lanternfly-vineyard-risk.json";
import "../../components/models/spotted-lanternfly-vineyard-risk/main.css";
import { LayerProps } from 'react-map-gl/mapbox';

const h1Text = "Spotted Lantern Fly Vineyard Risk Index";

export interface Location {
  name?: string,
  latitude: number,
  longitude: number
}

const newaStationsLayerStyle: LayerProps = {
  id: 'newa',
  type: 'symbol',
  source: 'composite',
  layout: {
    'icon-image': [
      'step',
      ['zoom'],
      ['case', ['==', ['get', 'isIcao'], true], 'airport', 'newa'],
      22,
      ['case', ['==', ['get', 'isIcao'], true], 'airport', 'newa'],
    ],
    'text-field': ['step', ['zoom'], '', 8, ['to-string', ['get', 'name']]],
    'text-size': 11,
    'text-anchor': 'bottom',
    'text-offset': [0, -0.7],
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-size': ['case', ['==', ['get', 'isIcao'], false], 1.8, 1],
  },
  minzoom: 10,
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'icon-emissive-strength': 100,
    'text-halo-width': 2,
    'text-halo-blur': 2,
    'text-halo-color': 'rgba(255, 255, 255, 0.9)', // White halo for readability
  },
};


const DEFAULT_LAT_LONG: Location = { name: 'Geneva (default)', latitude: 42.8767, longitude: -77.031 };

// Risk color stops for the 10 deciles (levels 1–10), resampled from the old
// 0–4 scale's ramp so the tool stays visually familiar. The old ramp collapsed
// 3.0/3.5/4.0 to one yellow, so the top three stops here share yellow and yellow
// shows from ~80% rather than only at 100. Keep in sync with riskColors in map.tsx.
const RISK_STOPS: [number, number, number][] = [
  [61, 66, 128], [53, 95, 141], [46, 110, 142], [44, 133, 138], [53, 183, 121],
  [126, 209, 78], [209, 225, 49], [253, 231, 37], [253, 231, 37], [253, 231, 37],
];

// Map fill colors, mirroring the `step` expression in map.tsx: index 0 covers
// <10 (the NA-adjacent range, transparent); indices 1–10 are the deciles at
// 0.7 alpha. Kept in sync with the map via the shared RISK_STOPS palette.
const RISK_FILL_COLORS = [
  'rgba(68,1,84, 0)',
  ...RISK_STOPS.map(([r, g, b]) => `rgba(${r},${g},${b}, .7)`),
];

// The exact color the map paints for a 0–100 value (same stepping, no hatch).
const riskFillColor = (value: number): string =>
  RISK_FILL_COLORS[Math.min(10, Math.max(0, Math.floor(value / 10)))];

const SLFVineyardRiskContent: React.FC = () => {
  // Station state
  const { stationList, station, setStation, geoJSON } = useStations({ filter: { state: 'NY' } });

  // Location state
  const [location, setLocation] = useState<Location>(() => {
    if (!station) {
      return DEFAULT_LAT_LONG;
    }
    return {
      name: station.name,
      latitude: station.lat,
      longitude: station.lon
    };
  });
  const [riskIndex, setRiskIndex] = useState<number | null>(null);
  const [locationInput, setLocationInput] = useState<string>('');
  const [mapReady, setMapReady] = useState(false);
  const [shouldFlyTo, setShouldFlyTo] = useState(false);
  const mapRef = useRef<SLFMapHandle>(null);

  // Tracking
  useTracking({ modelId: "SLF Vineyard Risk Index" });

  // Card expansion state
  const [expandedCards, setExpandedCards] = useState({
    riskResult: true,
    locationDetails: true,
    riskLegend: true,
  });

  const toggleExpand = (card: string) => {
    setExpandedCards((prevState) => ({
      ...prevState,
      [card]: !prevState[card],
    }));
  };

  const mapCustomHeight = "400px";

  // Centralized effect: When location changes and map is ready, fly to location and calculate risk
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current.getMap();
    if (!map || !map.isStyleLoaded()) return;

    // If we should fly to the location (for user interactions), do so and wait for idle
    if (shouldFlyTo) {
      const handleMapIdle = () => {
        if (map.isStyleLoaded()) {
          mapRef.current?.queryRiskAtLocation(location.latitude, location.longitude);
          map.off('idle', handleMapIdle);
        }
      };

      map.flyTo({
        center: [location.longitude, location.latitude],
        zoom: 11,
        duration: 750,
      });

      map.on('idle', handleMapIdle);
      setShouldFlyTo(false);
    } else {
      // For initial load or when map becomes ready, just query the current location
      mapRef.current.queryRiskAtLocation(location.latitude, location.longitude);
    }
  }, [location, mapReady, shouldFlyTo]);

  // Parse latitude and longitude from various string formats
  const parseLatLng = (input: string): { lat: number; lng: number } | null => {
    // Remove extra whitespace
    const cleaned = input.trim();

    // Try various formats:
    // 1. "40.7128, -74.0060" or "40.7128,-74.0060"
    // 2. "40.7128 -74.0060"
    // Pattern 1: Simple decimal degrees with comma or space
    const simplePattern = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/;
    const simpleMatch = cleaned.match(simplePattern);
    if (simpleMatch) {
      const lat = parseFloat(simpleMatch[1]);
      const lng = parseFloat(simpleMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }

    return null;
  };

  // Handler for coordinate input submission
  const handleCoordinateSubmit = (inputValue: string) => {
    const parsed = parseLatLng(inputValue);
    if (parsed) {
      setLocation({ latitude: parsed.lat, longitude: parsed.lng });
      setShouldFlyTo(true);
      setLocationInput('');
    } else if (inputValue.trim() !== '') {
      alert('Unable to parse coordinates. Please use format like: 40.7128, -74.0060');
    }
  };

  // Handler for input blur event
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleCoordinateSubmit(e.target.value);
  };

  // Handler for input keydown event
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCoordinateSubmit(e.currentTarget.value);
    }
  };

  // Handler for station selection
  const handleStationSelect = (selectedStation: any) => {
    setStation(selectedStation);
    if (selectedStation) {
      const newLocation = {
        name: selectedStation.name,
        latitude: selectedStation.lat,
        longitude: selectedStation.lon
      };
      setLocation(newLocation);
      setShouldFlyTo(true);
    }
  };

  // Handler for station click on map
  const handleStationMapClick = (stationId: string) => {
    // Look up the station by ID
    const clickedStation = stationList[0].options.find(s => s.id === stationId);
    if (clickedStation) {
      handleStationSelect(clickedStation);
    } else {
      console.warn('Station not found in list:', stationId);
    }
  };

  // Handler for regular map location click (non-station)
  const handleMapLocationSelect = (lat: number, lng: number) => {
    setLocation({ latitude: lat, longitude: lng });
    setShouldFlyTo(true);
  };

  // Closest-matching category (0–4: Negligible … Severe) for the 0–100 index,
  // by 20% bands — the equivalent of the old 0–8 scale's label lookup.
  const riskCategory = riskIndex !== null
    ? Math.min(4, Math.floor(riskIndex / 20))
    : 0;

  return (
    <>
      <Header/>
      <SubHeader h1Text={h1Text} references={Metadata.references}/>
      <div>
        <main id="main">
          <div id="app-container" className="flex flex-wrap justify-center gap-5 max-w-[1600px] mx-auto px-4">

            {/* Location Selection Card */}
            <div id="location-details" className="card-location shadow-md mb-5 p-2.5 bg-light-300">
              <h1 className="card-header" onClick={() => toggleExpand("locationDetails")}>
                <span>Location Selection</span>
                <span>{expandedCards.locationDetails ? "▼" : "►"}</span>
              </h1>
              {expandedCards.locationDetails && (
                <div className="card-location-content">
                  <p className="ml-4 mr-r">
                    Select a NEWA Station from the list, enter coordinates manually, or click on the map to choose a
                    location.
                  </p>
                  <div className="flex flex-row gap-4 items-center justify-between mb-4 p-4">
                    <div className="z-50 w-full md:w-1/2">
                      <StationsListing
                        options={stationList}
                        value={station}
                        setValue={handleStationSelect}
                        label={'Stations'}
                      />
                    </div>
                    <div className="w-full md:w-1/2">
                      <label className="block text-gray-700 text-sm mb-0 pb-0" htmlFor="locationDetails">Latitude and
                        Longitude</label>

                      <div className=" flex flex-col gap-2 mt-5 mb-4 justify-center">
                        <input
                          type="text"
                          className="w-full max-w-lg h-9 rounded-md"
                          style={{ borderColor: 'rgba(209, 213, 219, var(--tw-border-opacity))' }}
                          id="locationDetails"
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          onBlur={handleInputBlur}
                          onKeyDown={handleInputKeyDown}
                          placeholder="Enter decimal degrees"
                        />
                      </div>
                    </div>
                  </div>

                  {location && (
                    <div className="mt-0 flex items-center justify-center">
                      <span className="flex mr-2 hidden md:inline items-center">Selected Location:</span>
                      <input
                        value={`${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`}
                        onChange={() => {}}
                        className="text-gray-500 bg-gray-200 justify-center p-1 mr-4"
                      />
                    </div>
                  )}
                  <br/>
                  <div className="card-location-map-container relative">
                    <Map
                      ref={mapRef}
                      initialViewState={{
                        ...location,
                        zoom: 9,
                      }}
                      customHeight={mapCustomHeight}
                      onLocationSelect={handleMapLocationSelect}
                      location={location}
                      onRiskCalculated={(risk) => {
                        console.log("Risk calculated:", risk);
                        setRiskIndex(risk);
                      }}
                      onMapReady={() => setMapReady(true)}
                      onStationClick={handleStationMapClick}
                      geoJSON={(geoJSON as FeatureCollection)}
                      layerStyle={newaStationsLayerStyle}
                    />
                    <div
                      className="risk-legend-content absolute bottom-20 md:bottom-[50px] right-8 sm:right-5 bg-white rounded shadow-[0_1px_10px_rgba(55,55,55,0.5)] p-2.5 z-10 font-[12px/20px_'Helvetica_Neue',Arial,Helvetica,sans-serif]">
                      <h4 className="risk-legend-title mb-0 md:mb-4">Risk Index</h4>
                      <div className="flex items-center gap-[15px] relative">
                        <div className="legend-ticks flex flex-col-reverse justify-between h-[calc(300px+0.5em)]">
                          {RISK_STOPS.map((c, i) => (
                            <div className="legend-tick" key={i}>
                              <span style={{ backgroundColor: `rgb(${c[0]},${c[1]},${c[2]})` }}></span>
                              {i === 0 ? '0 Negligible' : i === RISK_STOPS.length - 1 ? '100 Severe' : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Combined Risk Index and Legend Card */}
            <div id="risk-result" className="card-risk h-full max-h-[750px] shadow-md mb-5 p-2.5 bg-light-300">
              <h1 className="card-header" onClick={() => toggleExpand("riskResult")}>
                <span>Risk Index</span>
                <span>{expandedCards.riskResult ? "▼" : "►"}</span>
              </h1>
              {expandedCards.riskResult && (
                <div>
                  {riskIndex !== null && (
                    <div
                      className="m-4 pl-2 pr-2 pt-8 pb-8 items-center flex risk-indicator-box">
                      <span
                        className="w-8 h-8 mr-4 rounded"
                        style={{ backgroundColor: riskFillColor(riskIndex) }}
                      />
                      <strong className="bg-white p-1">{riskIndex}</strong>
                      <strong className="ml-4 bg-white p-1">{Metadata.riskIndices[riskCategory].level}</strong>
                    </div>
                  )}
                  <div className="p-4 risk-description">

                    <div className="risk-description-container">
                      {riskIndex !== null ? (
                        <div className="risk-description-content">
                          {Metadata.riskIndices[riskCategory].description}
                          <p className={"text-xs"}>
                            In case of infestation, see <a
                            href={Metadata.moreInfo[0].url}>{Metadata.moreInfo[0].title}</a>
                          </p>
                        </div>
                      ) : (
                        <div className="risk-description-placeholder">
                          Select a location to calculate risk
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </>
  );
};

// const SLFVineyardRisk: React.FC = () => (
//   <PageLoadingProvider>
//     <SLFVineyardRiskContent></SLFVineyardRiskContent>
//   </PageLoadingProvider>
// )

export default SLFVineyardRiskContent;
